import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FeedbackRoundAsset } from '@brafton/feedback-api';
import { ROUND_ASSET_TYPE_OPTIONS } from '../add-asset-drawer/add-asset-drawer';

/** A mockup resolved for display: its URL and a derived file-name label. */
interface MockupItem {
  url: string;
  name: string;
}

/** A hovered mockup's enlarged copy: the mockup, and where it floats on screen. */
interface ZoomedMockup extends MockupItem {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** How much a hovered mockup is enlarged. */
const MOCKUP_ZOOM_SCALE = 1.5;

/**
 * Collapsible "Mockups & Assets" panel for the project detail page: an accordion
 * header (chevron, count, Add asset and Upload actions) over a grid of the round's
 * mockups, each scaling up on hover, and the list of its assets (documents added as
 * links or uploads). Presentational — the host passes the round's `mockupUrls` and
 * `assets` and handles the `upload` and `addAsset` actions.
 */
@Component({
  selector: 'app-mockups-panel',
  templateUrl: './mockups-panel.html',
  styleUrl: './mockups-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe],
  host: {
    // The enlarged copy is placed from the card's position, so moving the page drops it.
    '(window:scroll)': 'hideZoom()',
    '(window:resize)': 'hideZoom()',
  },
})
export class MockupsPanel {
  /** The mockup image URLs uploaded to the feedback round. */
  readonly mockups = input<string[]>([]);

  /** True while the round (and so its mockups and assets) is still loading. */
  readonly loading = input(false);

  /** Emitted when the Upload button is clicked. */
  readonly upload = output<void>();

  /** The round's assets (documents added as links or uploads), oldest first. */
  readonly assets = input<FeedbackRoundAsset[]>([]);

  /** Emitted when the Add asset button is clicked. */
  readonly addAsset = output<void>();

  /** Emitted with the asset whose remove button was clicked; the host removes it. */
  readonly removeAsset = output<FeedbackRoundAsset>();

  /** Assets being removed right now, whose remove buttons are disabled. */
  readonly removingAssetIds = input<string[]>([]);

  /** Assets newest first, each with its type in words. */
  protected readonly assetItems = computed(() =>
    [...this.assets()]
      .reverse()
      .map((asset) => ({
        ...asset,
        typeLabel: ROUND_ASSET_TYPE_OPTIONS.find((option) => option.value === asset.assetType)?.label ?? 'Other',
      })),
  );

  protected onAddAsset(event: Event): void {
    // Like Upload: a header button, so it mustn't toggle the accordion.
    event.stopPropagation();
    this.addAsset.emit();
  }

  /** Whether the panel is expanded (collapsed by default). */
  protected readonly open = signal(false);

  /**
   * The mockup shown enlarged over its card while hovered or focused, positioned from
   * the card. A floating copy rather than scaling the card itself: the strip scrolls,
   * and a scrolling box would clip a card that grows past its edges.
   */
  protected readonly zoomedMockup = signal<ZoomedMockup | null>(null);

  protected showZoom(event: Event, item: MockupItem): void {
    const cardRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const zoomedWidth = cardRect.width * MOCKUP_ZOOM_SCALE;
    const zoomedHeight = cardRect.height * MOCKUP_ZOOM_SCALE;
    const viewportWidth = typeof window === 'undefined' ? zoomedWidth : window.innerWidth;
    const viewportHeight = typeof window === 'undefined' ? zoomedHeight : window.innerHeight;
    // Centered on the card, then kept inside the viewport.
    const centeredLeft = cardRect.left - (zoomedWidth - cardRect.width) / 2;
    const centeredTop = cardRect.top - (zoomedHeight - cardRect.height) / 2;
    this.zoomedMockup.set({
      ...item,
      left: Math.round(Math.max(8, Math.min(centeredLeft, viewportWidth - zoomedWidth - 8))),
      top: Math.round(Math.max(8, Math.min(centeredTop, viewportHeight - zoomedHeight - 8))),
      width: Math.round(zoomedWidth),
      height: Math.round(zoomedHeight),
    });
  }

  protected hideZoom(): void {
    this.zoomedMockup.set(null);
  }

  protected readonly items = computed<MockupItem[]>(() =>
    this.mockups().map((url) => ({ url, name: this.fileName(url) })),
  );

  protected toggle(): void {
    this.open.update((isOpen) => !isOpen);
  }

  protected onUpload(event: Event): void {
    // Don't toggle the accordion when the Upload button (in the header) is hit.
    event.stopPropagation();
    this.upload.emit();
  }

  /** The trailing file name of a URL, for the mockup label. */
  private fileName(url: string): string {
    try {
      const path = new URL(url).pathname;
      return decodeURIComponent(path.split('/').filter(Boolean).pop() ?? url);
    } catch {
      return url.split('/').filter(Boolean).pop() ?? url;
    }
  }
}
